import type {
    Campaign,
    GetCampaignArgs,
    GetCampaignResult,
    GetCampaignSnapshotsArgs,
    GetCampaignSnapshotsResult,
    PatchCampaignArgs,
    SearchCampaignArgs,
    SearchCampaignResult
} from '@admin/widgets-engine/@types';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const apiRoot = {
    widgetEngine: 'wo/api/widgets/engine'
};

const actionUrl = {
    widgetEngine: {
        create: () => apiRoot.widgetEngine,
        delete: (id: number) => `${apiRoot.widgetEngine}/${id}`,
        get: (id: number) => `${apiRoot.widgetEngine}/${id}`,
        patch: (ids: number[]) => `${apiRoot.widgetEngine}?${ids.map(id => `ids=${id}`).join('&')}`,
        search: (query: URLSearchParams) => `${apiRoot.widgetEngine}/find?${query}`,
        update: (id: number, createSnapshot: boolean) =>
            `${apiRoot.widgetEngine}/${id}?createSnapshot=${createSnapshot}`,
        snapshots: {
            create: () => `${apiRoot.widgetEngine}/snapshots`,
            delete: (id: number) => `${apiRoot.widgetEngine}/snapshots/${id}`,
            get: (parentId: number) => `${apiRoot.widgetEngine}/${parentId}/snapshots`,
            restore: (id: number) => `${apiRoot.widgetEngine}/snapshots/${id}`
        }
    }
};

export type SearchQueryKey = 'name' | 'productAreaId' | 'userId' | 'isPreview' | 'page' | 'pageSize';

export const getQuery = {
    search: (args: SearchCampaignArgs) => {
        const query = new URLSearchParams();
        const append = (name: SearchQueryKey, value: string) => query.append(name, value);

        if (args.name != null && args.name.length > 0) {
            append('name', args.name!);
        }
        if (args.productAreaID != null) {
            append('productAreaId', args.productAreaID!.toString());
        }
        if (args.userID != null) {
            append('userId', args.userID!.toString());
        }
        if (args.isPreview) {
            append('isPreview', args.isPreview!.toString());
        }
        if (args.page != null && args.page > 0) {
            append('page', args.page.toString());
        }
        if (args.pageSize != null && args.pageSize > 0) {
            append('pageSize', args.pageSize.toString());
        }

        return query;
    }
};

/** See: https://github.com/chbrown/rfc6902#operation */
interface PatchItem {
    op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
    from?: string;
    path?: string;
    value?: string | boolean;
}

// TODO: Move to a separate file?
function getPatchBody(patch: Partial<PatchCampaignArgs>): PatchItem[] {
    const res: PatchItem[] = [];
    if (patch.isPreview != null) {
        res.push({
            op: 'replace',
            path: '/isPreview',
            value: patch.isPreview
        });
    }

    return res;
}

// TODO: Extract helper for constructing URLs

// TODO: Consider extract HeroCampaign related code to separate file
export const adminApi = createApi({
    reducerPath: 'admin/api',
    baseQuery: fetchBaseQuery({ baseUrl: '/' }),
    tagTypes: ['Widget', 'WidgetSnapshot'],
    endpoints: builder => ({
        widgetEngineGet: builder.query<GetCampaignResult, GetCampaignArgs>({
            query: args => actionUrl.widgetEngine.get(args.id),
            providesTags: result => (result ? [{ type: 'Widget' as const, id: result.id }, 'Widget'] : ['Widget'])
        }),

        widgetEngineSearch: builder.query<SearchCampaignResult, SearchCampaignArgs>({
            query: args => {
                const query = getQuery.search(args);
                return actionUrl.widgetEngine.search(query);
            },
            providesTags: result =>
                result ? [...result.items.map(({ id }) => ({ type: 'Widget' as const, id })), 'Widget'] : ['Widget']
        }),

        widgetEngineCreate: builder.mutation<{ id: number }, Campaign>({
            query: ({ ...patch }) => ({ url: actionUrl.widgetEngine.create(), method: 'POST', body: patch }),
            invalidatesTags: ['Widget']
        }),

        widgetEngineUpdate: builder.mutation<void, Campaign & { id: number; createSnapshot: boolean }>({
            query: ({ id, createSnapshot, ...patch }) => ({
                url: actionUrl.widgetEngine.update(id, createSnapshot),
                method: 'PUT',
                body: patch
            }),
            invalidatesTags: (result, error, args) =>
                args?.id ? [{ type: 'Widget' as const, id: args.id }, 'WidgetSnapshot'] : ['Widget', 'WidgetSnapshot']
        }),

        widgetEnginePatch: builder.mutation<void, Partial<PatchCampaignArgs> & Pick<PatchCampaignArgs, 'ids'>>({
            query: ({ ids, ...patch }) => ({
                url: actionUrl.widgetEngine.patch(ids),
                method: 'PATCH',
                body: getPatchBody(patch)
            }),
            invalidatesTags: (result, error, args) =>
                args.ids ? [...args.ids.map(id => ({ type: 'Widget' as const, id }))] : ['Widget']
        }),

        widgetEngineDelete: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({ url: actionUrl.widgetEngine.delete(id), method: 'DELETE' }),
            invalidatesTags: ['Widget']
        }),

        widgetEngineCreateSnapshot: builder.mutation<{ id: number }, Campaign>({
            query: ({ ...patch }) => ({ url: actionUrl.widgetEngine.snapshots.create(), method: 'POST', body: patch }),
            invalidatesTags: ['WidgetSnapshot']
        }),

        widgetEngineGetSnapshots: builder.query<GetCampaignSnapshotsResult, GetCampaignSnapshotsArgs>({
            query: args => {
                return actionUrl.widgetEngine.snapshots.get(args.parentId);
            },
            providesTags: result =>
                result
                    ? [{ type: 'WidgetSnapshot' as const, id: result.parentId }, 'WidgetSnapshot']
                    : ['WidgetSnapshot']
        }),

        widgetEngineDeleteSnapshot: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({ url: actionUrl.widgetEngine.snapshots.delete(id), method: 'DELETE' }),
            invalidatesTags: ['WidgetSnapshot']
        }),

        widgetEngineRestoreSnapshot: builder.mutation<void, { id: number; parentId: number }>({
            query: ({ id }) => ({ url: actionUrl.widgetEngine.snapshots.restore(id), method: 'PUT' }),
            invalidatesTags: (result, error, args) => [{ type: 'Widget' as const, id: args.parentId }]
        })
    })
});

export type AdminApiState = Parameters<typeof adminApi.reducer>['0'];
